import { SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import {
  Col,
  Row,
  Table,
  Tag,
  Form,
  Tabs,
  Switch,
  message,
  Modal,
  ConfigProvider,
  Divider,
} from "antd";
import React, { useEffect, useState } from "react";
import Button from "../../../components/Button/Button";
import { CardContainer } from "../../../components/Card/CardContainer";
import { RangePicker } from "../../../components/DatePicker/DatePicker";
import Input from "../../../components/Input/Input";
import { FlexCol } from "../../../components/Container/Container";
import Text from "../../../components/Text/Text";
import moment from "moment";
import { color } from "../../../resource";
import { useNavigate } from "react-router-dom";
import {
  deleteCampaign,
  getCampaign,
  updateStatusCampaign,
} from "../../../datasource/CampaignDatasource";
import icon from "../../../resource/icon";
import { useRecoilValue } from "recoil";
import { roleAtom } from "../../../store/RoleAtom";
import { checkPermission } from "../../../components/Permission/Permission";
import {
  campaignStatusColorList,
  campaignStatusNameList,
  campaignStatusTextColorList,
} from "../../../definitions/campaign";
import { useMessageContext } from "../../../context/MessageProvider";

type FixedType = "left" | "right" | boolean;
const REQUEST_DMY = "YYYY-MM-DD";

export const IndexCampaignPoint: React.FC = () => {
  const { messageApi } = useMessageContext();
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const { company, firstname, lastname } = userProfile;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(0);
  const [countStatus, setCountStatus] = useState<any>({});
  const [keyword, setKeyword] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [dateFilter, setDateFilter] = useState<any>();
  const [modalConfirm, setModalConfirm] = useState<{
    open: boolean;
    action: boolean;
    name: string;
    campaignId: string;
  }>({
    open: false,
    action: false,
    name: "",
    campaignId: "",
  });

  const resetPage = () => setPage(1);

  const roleData = useRecoilValue(roleAtom);
  const hasPermission = checkPermission(["campaignPoint", "approve"], roleData);

  useEffect(() => {
    if (!loading) fetchCampaign();
  }, [keyword, dateFilter, statusFilter, page, pageSize]);

  const fetchCampaign = async () => {
    setLoading(true);
    await getCampaign({
      company,
      searchText: keyword,
      status: statusFilter,
      startDate: dateFilter && dateFilter[0] ? dateFilter[0].format(REQUEST_DMY) : undefined,
      endDate: dateFilter && dateFilter[1] ? dateFilter[1].format(REQUEST_DMY) : undefined,
      page,
      take: pageSize,
    })
      .then((res) => {
        setData(res.data || []);
        setCount(res.count || 0);
        setCountStatus(res.statusCount);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const ActionBtn = ({ onClick, icon }: any) => {
    return (
      <Col>
        <div className='btn btn-icon btn-light btn-hover-primary btn-sm' onClick={onClick}>
          <span
            className='svg-icon svg-icon-primary svg-icon-2x'
            style={{ color: color["primary"] }}
          >
            {icon}
          </span>
        </div>
      </Col>
    );
  };

  const tabsItems = [
    {
      label: `ทั้งหมด (${countStatus?.ALL || 0})`,
      key: "ALL",
    },
    {
      label: `รออนุมัติ (${countStatus?.WAIT_APPROVE || 0})`,
      key: "WAIT_APPROVE",
    },
    {
      label: `รอเปิดใช้งาน (${countStatus?.WAIT_START || 0})`,
      key: "WAIT_START",
    },
    {
      label: `เปิดใช้งาน (${countStatus?.ENABLE || 0})`,
      key: "ENABLE",
    },
    {
      label: `ปิดใช้งาน (${countStatus?.DISABLED || 0})`,
      key: "DISABLED",
    },
    {
      label: `หมดอายุ (${countStatus?.EXPIRED || 0})`,
      key: "EXPIRED",
    },
  ];

  const PageTitle = () => {
    return (
      <Form>
        <Row align='middle' gutter={16}>
          <Col className='gutter-row' span={10}>
            <div>
              <span
                className='card-label font-weight-bolder text-dark'
                style={{ fontSize: 20, fontWeight: "bold" }}
              >
                แคมเปญพอยท์
              </span>
            </div>
          </Col>

          <Col span={5}>
            <Input
              placeholder='รหัส/ชื่อแคมเปญ'
              suffix={<SearchOutlined />}
              defaultValue={keyword}
              onPressEnter={(e) => {
                const value = (e.target as HTMLTextAreaElement).value;
                setKeyword(value);
                resetPage();
              }}
              onChange={(e) => {
                const value = (e.target as HTMLTextAreaElement).value;
                if (!value) {
                  setKeyword("");
                  resetPage();
                }
              }}
            />
          </Col>
          <Col span={5}>
            <RangePicker
              enablePast
              allowEmpty={[true, true]}
              onChange={(dates, dateString) => {
                setDateFilter(dates);
              }}
              value={dateFilter}
            />
          </Col>
          <Col className='gutter-row' xl={4} sm={6}>
            {checkPermission(["campaignPoint", "create"], roleData) && (
              <Button
                type='primary'
                title='+ สร้างแคมเปญ'
                height={40}
                onClick={() => navigate("/crm/createCampaignPoint")}
                size='large'
              />
            )}
          </Col>
        </Row>
      </Form>
    );
  };

  const columns = [
    {
      title: "ชื่อแคมเปญพอยท์",
      dataIndex: "campaignName",
      key: "campaignName",
      width: "18%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{row.campaignName}</Text>
              <Text level={6} color='Text3'>
                {row.campaignCode}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "ระยะเวลา",
      dataIndex: "period",
      key: "period",
      width: "6%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{`${row.startDate ? moment(row.startDate).format("DD/MM/YYYY") : ""} - ${
                row.endDate ? moment(row.endDate).format("DD/MM/YYYY") : ""
              }`}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "รูปแบบ",
      dataIndex: "campaignProgramType",
      key: "campaignProgramType",
      width: "8%",
      render: (value: any, row: any, index: number) => {
        const mapText = {
          ONLY_DEALER: "ให้ Dealer เท่านั้น",
          ONLY_SUB_DEALER: "ให้ Sub-Dealer เท่านั้น",
        };
        return {
          children: (
            <FlexCol>
              <Text>{mapText[value] || "-"}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "พอยท์โปรแกรม",
      dataIndex: "countCampaignPrograms",
      key: "countCampaignPrograms",
      width: "8%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value || 0}</Text>
              <Text level={6} color='Text3'>
                โปรแกรม
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "จำนวนร้านค้า",
      dataIndex: "countCampaignProgramDealers",
      key: "countCampaignProgramDealers",
      width: "5%",
      render: (value: any[], row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value || 0}</Text>
              <Text level={6} color='Text3'>
                ร้านค้า
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "อัปเดตโดย",
      dataIndex: "updateAt",
      key: "updateAt",
      width: "8%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{moment(value).format("DD/MM/YYYY")}</Text>
              <Text level={6} color='Text3'>
                {row.updateBy}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      key: "status",
      fixed: "right" as FixedType | undefined,
      width: "5%",
      render: (status: any, row: any, index: number) => {
        return {
          children: (
            <>
              <Tag color={campaignStatusColorList[status]} style={{ fontWeight: 700 }}>
                <Text color={campaignStatusTextColorList[status]} level={6} fontWeight={700}>
                  {campaignStatusNameList[status]}
                </Text>
              </Tag>
            </>
          ),
        };
      },
    },
    {
      title: "เปิด/ปิดใช้งาน",
      dataIndex: "isEnabled",
      key: "isEnabled",
      fixed: "right" as FixedType | undefined,
      width: "5%",
      hidden: !hasPermission,
      render: (isEnabled: any, row: any, index: number) => {
        const isExpired = moment(row.endDate).isBefore(moment());
        const isStart = moment(row.startDate).isBefore(moment());
        const isLocked = isExpired || (isStart && isEnabled);
        return {
          children: (
            <>
              <Switch
                checked={isEnabled}
                onClick={async (checked) => {
                  setModalConfirm({
                    open: true,
                    action: checked,
                    name: row.campaignName,
                    campaignId: row.campaignId,
                  });
                }}
                disabled={!hasPermission || isLocked}
              />
            </>
          ),
        };
      },
    },
    {
      title: "จัดการ",
      dataIndex: "action",
      key: "action",
      fixed: "right" as FixedType | undefined,
      width: "7%",
      render: (value: any, row: any, index: number) => {
        const isExpired = moment(row.endDate).isBefore(moment());
        const isStart = moment(row.startDate).isBefore(moment());
        const isApproved = row?.isApproved;
        const canBeDeleted =
          checkPermission(["campaignPoint", "delete"], roleData) &&
          !isExpired &&
          (!isStart || !isApproved) &&
          (!row?.orderCount || row?.orderCount <= 0);
        return {
          children: (
            <>
              <Row justify='start' gutter={8}>
                <ActionBtn
                  onClick={() => navigate("/crm/campaignPoint/" + row.campaignId)}
                  icon={
                    <UnorderedListOutlined style={{ width: 16, height: 16, fontWeight: 700 }} />
                  }
                />
                {/* {checkPermission(["campaignPoint", "approve"], roleData) && (
                  <ActionBtn
                    onClick={() => navigate("/crm/campaignPoint/history/" + row.campaignId)}
                    icon={<img src={icon.crmPointTransaction} style={{ width: 16, height: 16 }} />}
                  />
                )} */}
                <ActionBtn
                  onClick={() => {
                    const query = new URLSearchParams({ copy_id: row.campaignId }).toString();
                    Modal.confirm({
                      icon: null,
                      centered: true,
                      width: 500,
                      title: (
                        <Text fontWeight={700} level={2}>
                          ยืนยันการคัดลอกแคมเปญ
                        </Text>
                      ),
                      content: (
                        <Text color='Text3'>
                          โปรดตรวจสอบรายละเอียดแคมเปญ “{row.campaignName}” ({row.campaignCode})
                          ที่ต้องการคัดลอก ก่อนกดยืนยัน
                        </Text>
                      ),
                      okText: "ยืนยัน",
                      cancelText: "ยกเลิก",
                      cancelButtonProps: {
                        style: { borderColor: color.primary, color: color.primary },
                      },
                      onOk: () => navigate("/crm/createCampaignPoint?" + query),
                    });
                  }}
                  icon={<img src={icon.dupplicate} style={{ width: 16, height: 16 }} />}
                />
                <ActionBtn
                  disabled={!canBeDeleted}
                  icon={
                    <img
                      src={!canBeDeleted ? icon.deleteGray : icon.delete}
                      style={{ width: 16, height: 16 }}
                    />
                  }
                  onClick={() =>
                    !canBeDeleted
                      ? null
                      : Modal.confirm({
                          icon: null,
                          width: 470,
                          title: (
                            <Text fontWeight={700} level={2}>
                              ยืนยันการลบ
                            </Text>
                          ),
                          content: (
                            <FlexCol>
                              <Text fontWeight={700} level={3}>
                                “{row.campaignName}”
                              </Text>
                              <Text color='Text3'>โปรดตรวจสอบรายละเอียดอีกครั้งก่อนกดยืนยัน</Text>
                            </FlexCol>
                          ),
                          centered: true,
                          okText: "ยืนยัน",
                          cancelText: "ยกเลิก",
                          okButtonProps: {
                            style: {
                              borderColor: color.error,
                              color: color.white,
                              backgroundColor: color.error,
                            },
                          },
                          cancelButtonProps: {
                            style: { borderColor: color.error, color: color.error },
                          },
                          onOk: async () => {
                            await deleteCampaign({
                              campaignId: row.campaignId,
                              updateBy: firstname + " " + lastname,
                            })
                              .then((res) => {
                                fetchCampaign();
                                messageApi.open({
                                  type: "error",
                                  content: "ลบสำเร็จ",
                                  icon: <img src={icon.delete} style={{ width: 14, height: 16 }} />,
                                });
                              })
                              .catch(() => message.error("ลบแคมเปญไม่สำเร็จ"));
                          },
                        })
                  }
                />
              </Row>
            </>
          ),
        };
      },
    },
  ].filter((column) => !column.hidden);

  return (
    <>
      <CardContainer>
        <PageTitle />
        <br />
        <Tabs
          style={{ fontFamily: "IBM Plex Sans Thai" }}
          items={tabsItems}
          onChange={(key: string) => {
            setStatusFilter(key);
            resetPage();
          }}
        />
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#F3F6F9",
                fontFamily: "IBM Plex Sans Thai",
                fontSize: 15,
              },
            },
          }}
        >
          <Table
            className='rounded-lg'
            columns={columns}
            scroll={{ x: "1700px" }}
            dataSource={data || []}
            pagination={{
              position: ["bottomCenter"],
              pageSize: pageSize,
              current: page,
              total: count,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
              showSizeChanger: false,
            }}
            size='large'
            tableLayout='fixed'
            loading={loading}
          />
        </ConfigProvider>
      </CardContainer>
      {modalConfirm.open && (
        <Modal
          open={modalConfirm.open}
          onCancel={() => setModalConfirm({ ...modalConfirm, open: false })}
          centered
          okText='ยืนยัน'
          cancelText='ยกเลิก'
          onOk={async () => {
            await updateStatusCampaign({
              campaignId: modalConfirm.campaignId,
              enabled: modalConfirm.action,
              updateBy: `${firstname} ${lastname}`,
            }).then((res) => {
              if (res.success) {
                setModalConfirm({ ...modalConfirm, open: false });
                fetchCampaign();
                messageApi.success({
                  type: "success",
                  content: "บันทึกสำเร็จ",
                });
              }
            });
          }}
          cancelButtonProps={{ style: { borderColor: color.primary, color: color.primary } }}
        >
          <Text level={2} fontWeight={700}>
            ยืนยันการ{modalConfirm.action === true ? "เปิดใช้งาน" : "ปิดใช้งาน"}
          </Text>
          <br />
          <Text level={2} fontWeight={700}>
            {`“${modalConfirm.name}”`}
          </Text>
          <br />
          <Text color='Text3'>โปรดตรวจสอบรายละเอียดอีกครั้งก่อนกดยืนยัน</Text>
          <Divider size='small' />
        </Modal>
      )}
    </>
  );
};
export default IndexCampaignPoint;
